import React, { Fragment, useEffect, useState } from 'react';
import GlobalStyles from '../../styles/globalStyles';
import { ToastContainer } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  AgencyHomePage,
  AgencyInfluencersPage,
  AgencyProfilePage,
  AgencySettingsPage,
  ChangePasswordPage,
  ChatPage,
  ChatterInfluencersPage,
  ChatterProfilePage,
  ChatterSettingsPage,
  CollectionsPage,
  CollectionUsersPage,
  EmailVerificationPage,
  FactsPage,
  ForbiddenPage,
  ForgotPasswordPage,
  HomePage,
  IdVerificationPage,
  InfluencerAnalitycsPage,
  InfluencerFactsPage,
  InfluencerPage,
  InReviewPage,
  LoginPage,
  MassDMPage,
  MessagesPage,
  NotificationsPage,
  EditInfluencerPage,
  OrganizationAnalyticsPage,
  OrganizationChatterAnalyticsPage,
  OrganizationInfluencerAnalyticsPage,
  OrganizationMembersPage,
  MessageReviewsPage,
  PageNotFound,
  PersonalInfoPage,
  PrivacyPolicyPage,
  ProfilePage,
  ReferralsPage,
  ResetPasswordPage,
  SetMediaPricePage,
  SignUpPage,
  SplashPage,
  TelegramInfoPage,
  TermsAndConditionsPage,
  UpdatePasswordPage,
  UploadedBundlePage,
  UploadedFolderPage,
  UploadedMediaPage,
  UploadMediaPage,
  UploadPersonalityPage,
  UploadVoicePage,
  VaultPage,
  WalletPage,
  WithdrawalMethodPage,
  WithdrawPage,
  WritingStylePage,
} from '../../pages';
import { selectIsLoggedIn } from '../../store/slices/auth/slice';
import { AuthLayout, PrivateLayout } from '../../layouts';
import { fetchUserInfo } from '../../store/slices/user/asyncThunks';
import {
  ABOUT_YOU,
  AGENCY_HOME,
  AGENCY_INFLUENCERS,
  AGENCY_SETTINGS,
  CHANGE_PASSWORD,
  EMAIL_VERIFICATION,
  FACTS,
  FORBIDDEN,
  FORGOT_PASSWORD,
  HOME,
  ID_VERIFICATION,
  IN_REVIEW,
  INFLUENCER,
  INFLUENCER_ANALYTICS,
  INFLUENCER_FACTS,
  LOGIN,
  MEDIA_PRICE,
  PERSONAL_INFO,
  PERSONALITY,
  PRIVACY_POLICY,
  PROFILE,
  REFERRALS,
  RESET_PASSWORD,
  SIGN_UP,
  TELEGRAM_INFO,
  TERMS_AND_CONDITIONS,
  UPDATE_PASSWORD,
  UPLOAD_FREE_ASSISTED_PHOTOS,
  UPLOAD_FREE_ASSISTED_VIDEOS,
  UPLOAD_MEDIA,
  UPLOAD_PAID_ASSISTED_PHOTOS,
  UPLOAD_PAID_ASSISTED_VIDEOS,
  UPLOAD_VOICE,
  WALLET,
  WITHDRAW,
  WITHDRAWAL_METHOD,
  CHATTER_HOME,
  CHATTER_SETTINGS,
  CHATTER_MASS_DM,
  CHATTER_MESSAGES,
  CHATTER_CHAT,
  CHATTER_COLLECTIONS,
  CHATTER_COLLECTION_USERS,
  CHATTER_NOTIFICATIONS,
  CHATTER_EDIT,
  CHATTER_VAULT,
  CHATTER_VAULT_FOLDER,
  CHATTER_VAULT_BUNDLE,
  CHATTER_VAULT_MEDIA,
  ORGANIZATION_MEMBERS,
  ORGANIZATION_ANALYTICS,
  ORGANIZATION_CHATTER_ANALYTICS,
  ORGANIZATION_INFLUENCER_ANALYTICS,
  MESSAGE_REVIEWS,
} from './routes';
import PrivateOutlet from '../PrivateOutlet/PrivateOutlet';
import InfluencerRequestOutlet from '../InfluencerRequestOutlet/InfluencerRequestOutlet';
import {
  CRM_ORGANIZATION_MEMBER_ROLE,
  CRM_ORGANIZATION_MEMBER_ROLES,
  ENVIRONMENT,
  INFLUENCER_REQUEST_STATUS,
  LOGOUT,
  USER_ROLE,
} from '../../utils/constants';
import { selectUser, setDateRange } from '../../store/slices/user/slice';
import UploadAssistedPhotos from '../UploadAssistedPhotos/UploadAssistedPhotos';
import UploadAssistedVideos from '../UploadAssistedVideos/UploadAssistedVideos';
import config from '../../config';
import { dateToUTC, hasUtcRolledOverToNewDay } from '../../utils/date';
import { addDays, endOfDay, startOfMonth } from 'date-fns';
import { useSocket } from '../../hooks/useSocket';
import { authChannel } from '../../channels/authChannel';

const App = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const [minimumDurationPassed, setMinimumDurationPassed] = useState(false);
  const shouldConnectSocket = [
    CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
    CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
  ].includes(user?.role);
  useSocket(shouldConnectSocket);

  useEffect(() => {
    authChannel.onmessage = event => {
      if (event.data === LOGOUT) {
        dispatch({ type: LOGOUT });
      }
    };

    return () => authChannel.close();
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => setMinimumDurationPassed(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    dispatch(fetchUserInfo())
      .unwrap()
      .then(() => {})
      .catch(err => {
        // notifyError(err.message);
      });
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      if (config.environment === ENVIRONMENT.PRODUCTION)
        setTimeout(() => {
          window.Intercom('boot', {
            email: user.email,
            created_at: Math.floor(new Date(user.createdAt).getTime() / 1000),
            name: user.name,
            user_id: user.id,
          });
        }, 1000);
    } else {
      window.Intercom('shutdown');
    }
  }, [user]);

  useEffect(() => {
    if (user?.isSuperAdmin) {
      if (hasUtcRolledOverToNewDay()) {
        dispatch(
          setDateRange([
            dateToUTC(startOfMonth(new Date())).toISOString(),
            dateToUTC(addDays(endOfDay(new Date()), 1)).toISOString(),
          ]),
        );
      }
    }
  }, [user, dispatch]);

  return (
    <Fragment>
      <GlobalStyles />
      <ToastContainer newestOnTop={true} autoClose={3500} />
      {minimumDurationPassed && isLoggedIn !== null ? (
        <Routes>
          <Route element={<AuthLayout />}>
            <Route path={LOGIN} element={<LoginPage />} />
            <Route path={SIGN_UP} element={<SignUpPage />} />
            <Route path={FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
            <Route path={EMAIL_VERIFICATION} element={<EmailVerificationPage />} />
            <Route path={RESET_PASSWORD} element={<ResetPasswordPage />} />
          </Route>
          <Route element={<PrivateLayout hideNavigation />}>
            <Route element={<PrivateOutlet roles={[USER_ROLE.INFLUENCER_REQUEST]} />}>
              <Route element={<InfluencerRequestOutlet allowedStatuses={[INFLUENCER_REQUEST_STATUS.EMAIL_VERIFIED]} />}>
                <Route path={UPDATE_PASSWORD} element={<UpdatePasswordPage />} />
              </Route>
              <Route
                element={<InfluencerRequestOutlet allowedStatuses={[INFLUENCER_REQUEST_STATUS.REVIEW_ACCEPTED]} />}>
                <Route path={ID_VERIFICATION} element={<IdVerificationPage />} />
              </Route>
              <Route
                element={
                  <InfluencerRequestOutlet allowedStatuses={[INFLUENCER_REQUEST_STATUS.ID_DOCUMENT_VERIFIED]} />
                }>
                <Route path={TELEGRAM_INFO} element={<TelegramInfoPage />} />
              </Route>
              <Route
                element={
                  <InfluencerRequestOutlet allowedStatuses={[INFLUENCER_REQUEST_STATUS.TELEGRAM_INFO_CREATED]} />
                }>
                <Route path={PERSONALITY} element={<UploadPersonalityPage />} />
              </Route>
              <Route
                element={<InfluencerRequestOutlet allowedStatuses={[INFLUENCER_REQUEST_STATUS.PERSONALITY_CREATED]} />}>
                <Route path={ABOUT_YOU} element={<WritingStylePage />} />
              </Route>
              <Route
                element={
                  <InfluencerRequestOutlet allowedStatuses={[INFLUENCER_REQUEST_STATUS.WRITING_STYLE_CREATED]} />
                }>
                <Route path={UPLOAD_MEDIA} element={<UploadMediaPage />}>
                  {/* <Route path={UPLOAD_FREE_PHOTOS} element={<UploadPhotos />} />
                  <Route path={UPLOAD_PAID_PHOTOS} element={<UploadPhotos paid />} />
                  <Route path={UPLOAD_FREE_VIDEOS} element={<UploadVideos />} />
                  <Route path={UPLOAD_PAID_VIDEOS} element={<UploadVideos paid />} /> */}
                  <Route path={UPLOAD_FREE_ASSISTED_PHOTOS} element={<UploadAssistedPhotos />} />
                  <Route path={UPLOAD_PAID_ASSISTED_PHOTOS} element={<UploadAssistedPhotos paid />} />
                  <Route path={UPLOAD_FREE_ASSISTED_VIDEOS} element={<UploadAssistedVideos />} />
                  <Route path={UPLOAD_PAID_ASSISTED_VIDEOS} element={<UploadAssistedVideos paid />} />
                  {/* <Route path={UPLOAD_BUNDLES} element={<UploadBundles />} /> */}
                </Route>
              </Route>
              <Route element={<InfluencerRequestOutlet allowedStatuses={[INFLUENCER_REQUEST_STATUS.MEDIA_UPLOADED]} />}>
                <Route path={MEDIA_PRICE} element={<SetMediaPricePage />} />
              </Route>
              <Route
                element={<InfluencerRequestOutlet allowedStatuses={[INFLUENCER_REQUEST_STATUS.MEDIA_PRICE_SET]} />}>
                <Route path={UPLOAD_VOICE} element={<UploadVoicePage />} />
              </Route>
              <Route
                element={
                  <InfluencerRequestOutlet allowedStatuses={[INFLUENCER_REQUEST_STATUS.APPROVAL_IN_PROGRESS]} />
                }>
                <Route path={IN_REVIEW} element={<InReviewPage />} />
              </Route>
            </Route>
          </Route>
          <Route element={<PrivateLayout />}>
            <Route element={<PrivateOutlet roles={[USER_ROLE.AGENCY]} />}>
              <Route path={AGENCY_HOME} element={<AgencyHomePage />} />
              <Route path={AGENCY_INFLUENCERS} element={<AgencyInfluencersPage />} />
              <Route path={INFLUENCER} element={<InfluencerPage />}>
                <Route index element={<Navigate to={INFLUENCER_ANALYTICS} replace />} />
                <Route path={INFLUENCER_ANALYTICS} element={<InfluencerAnalitycsPage />} />
                <Route path={INFLUENCER_FACTS} element={<InfluencerFactsPage />} />
              </Route>
              <Route path={AGENCY_SETTINGS} element={<AgencySettingsPage />}>
                <Route index element={<Navigate to={PERSONAL_INFO} replace />} />
                <Route path={PERSONAL_INFO} element={<AgencyProfilePage />} />
                <Route path={CHANGE_PASSWORD} element={<ChangePasswordPage />} />
                <Route path={TERMS_AND_CONDITIONS} element={<TermsAndConditionsPage />} />
                <Route path={PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
              </Route>
            </Route>
            <Route element={<PrivateOutlet roles={[USER_ROLE.INFLUENCER]} />}>
              <Route path={HOME} element={<HomePage />} />
              <Route path={WALLET} element={<WalletPage />} />
              <Route path={WITHDRAW} element={<WithdrawPage />} />
              <Route path={WITHDRAWAL_METHOD} element={<WithdrawalMethodPage />} />
              <Route path={REFERRALS} element={<ReferralsPage />} />
              <Route path={PROFILE} element={<ProfilePage />}>
                <Route index element={<Navigate to={PERSONAL_INFO} replace />} />
                <Route path={PERSONAL_INFO} element={<PersonalInfoPage />} />
                <Route path={FACTS} element={<FactsPage />} />
                <Route path={CHANGE_PASSWORD} element={<ChangePasswordPage />} />
                <Route path={TERMS_AND_CONDITIONS} element={<TermsAndConditionsPage />} />
                <Route path={PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
              </Route>
            </Route>
            <Route element={<PrivateOutlet roles={CRM_ORGANIZATION_MEMBER_ROLES} />}>
              <Route path={CHATTER_HOME} element={<ChatterInfluencersPage />} />
              <Route path={CHATTER_SETTINGS} element={<ChatterSettingsPage />}>
                <Route index element={<Navigate to={PERSONAL_INFO} replace />} />
                <Route path={PERSONAL_INFO} element={<ChatterProfilePage />} />
                <Route path={CHANGE_PASSWORD} element={<ChangePasswordPage />} />
                <Route path={TERMS_AND_CONDITIONS} element={<TermsAndConditionsPage />} />
                <Route path={PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
              </Route>
            </Route>
            <Route
              element={
                <PrivateOutlet
                  roles={[
                    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
                    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
                    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
                    CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
                  ]}
                />
              }>
              <Route path={CHATTER_MASS_DM} element={<MassDMPage />} />
            </Route>
            <Route
              element={
                <PrivateOutlet
                  roles={[
                    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
                    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
                    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
                    CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
                    CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
                  ]}
                />
              }>
              <Route path={CHATTER_MESSAGES} element={<MessagesPage />}>
                <Route path={CHATTER_CHAT} element={<ChatPage />} />
              </Route>
            </Route>
            <Route
              element={
                <PrivateOutlet
                  roles={[
                    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
                    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
                    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
                    CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
                    CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
                    CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
                  ]}
                />
              }>
              <Route path={CHATTER_VAULT} element={<VaultPage />}>
                <Route index element={<Navigate to={CHATTER_VAULT_MEDIA.replace(':type', 'paid')} replace />} />
                <Route path={CHATTER_VAULT_MEDIA} element={<UploadedMediaPage />} />
                <Route path={CHATTER_VAULT_BUNDLE} element={<UploadedBundlePage />} />
                <Route path={CHATTER_VAULT_FOLDER} element={<UploadedFolderPage />} />
              </Route>
            </Route>
            <Route
              element={
                <PrivateOutlet
                  roles={[
                    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
                    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
                    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
                  ]}
                />
              }>
              <Route path={ORGANIZATION_MEMBERS} element={<OrganizationMembersPage />} />
              <Route path={ORGANIZATION_ANALYTICS} element={<OrganizationAnalyticsPage />}>
                <Route index element={<Navigate to={ORGANIZATION_CHATTER_ANALYTICS} replace />} />
                <Route path={ORGANIZATION_CHATTER_ANALYTICS} element={<OrganizationChatterAnalyticsPage />} />
              </Route>
              <Route path={CHATTER_COLLECTIONS} element={<CollectionsPage />}>
                <Route path={CHATTER_COLLECTION_USERS} element={<CollectionUsersPage />} />
              </Route>
            </Route>
            <Route
              element={
                <PrivateOutlet
                  roles={[
                    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
                    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
                    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
                    CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
                    CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
                  ]}
                />
              }>
              <Route path={CHATTER_NOTIFICATIONS} element={<NotificationsPage />} />
            </Route>
            <Route
              element={
                <PrivateOutlet roles={[CRM_ORGANIZATION_MEMBER_ROLE.OWNER, CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR]} />
              }>
              <Route path={ORGANIZATION_ANALYTICS} element={<OrganizationAnalyticsPage />}>
                <Route path={ORGANIZATION_INFLUENCER_ANALYTICS} element={<OrganizationInfluencerAnalyticsPage />} />
              </Route>
              <Route path={CHATTER_EDIT} element={<EditInfluencerPage />} />
            </Route>
            <Route
              element={
                <PrivateOutlet
                  roles={[
                    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
                    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
                    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
                    CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
                  ]}
                />
              }>
              <Route path={MESSAGE_REVIEWS} element={<MessageReviewsPage />} />
            </Route>
          </Route>
          <Route path={TERMS_AND_CONDITIONS} element={<TermsAndConditionsPage />} />
          <Route path={FORBIDDEN} element={<ForbiddenPage />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      ) : (
        <SplashPage />
      )}
    </Fragment>
  );
};

export default App;
